import { inject, Injectable, OnDestroy } from '@angular/core';
import { Campaign, CampaignNextApi } from '@fmnts/api/next';
import { FindByLiteralProp } from '@fmnts/core';
import { LegacyStore } from '@formunauts/next/data-access';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  merge,
  Observable,
  ReplaySubject,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

export enum CampaignActionName {
  FetchCampaignError = '[Campaign] Campaign Fetched Error',
  FetchCampaignListError = '[Campaign] Campaign List Fetched Error',
  UnsetCurrentCampaign = '[Campaign] Unset Current Campaign',
  SetCurrentCampaign = '[Campaign] Set Current Campaign',
}

interface ICampaignStoreAction {
  /** Name of the action */
  action: CampaignActionName;
  /** Action payload */
  payload?: unknown;
}

interface FetchCampaignError extends ICampaignStoreAction {
  action: CampaignActionName.FetchCampaignError;
  payload: {
    /** Error that occured */
    error: unknown;
    /** Id of the campaign that could not be fetched */
    id: Campaign['id'];
  };
}

interface FetchCampaignListError extends ICampaignStoreAction {
  action: CampaignActionName.FetchCampaignListError;
  payload: {
    /** Error that occured */
    error: unknown;
  };
}

interface ResetCurrentCampaign extends ICampaignStoreAction {
  action: CampaignActionName.UnsetCurrentCampaign;
}

interface SetCurrentCampaign extends ICampaignStoreAction {
  action: CampaignActionName.SetCurrentCampaign;
  payload: {
    // Id of the current campaign id
    id: Campaign['id'];
  };
}

/** Union of all possible campaign store actions */
type CampaignStoreAction =
  | FetchCampaignError
  | FetchCampaignListError
  | ResetCurrentCampaign
  | SetCurrentCampaign;

/** Used to extract the payload for a specific action */
type ActionPayload<Action> = FindByLiteralProp<
  CampaignStoreAction,
  Action,
  'action'
>['payload'];

/**
 * Service for accessing campaigns in next.
 */
@Injectable({ providedIn: 'root' })
export class NextCampaignStore implements OnDestroy {
  /** Emits store actions */
  private readonly _actions = new Subject<CampaignStoreAction>();

  protected readonly store = inject(LegacyStore);

  private readonly campaignNextApi = inject(CampaignNextApi);
  /** Emits and completes when destroyed */
  private readonly _destroyed = new ReplaySubject<boolean>(1);

  public readonly allCampaigns$: Observable<Campaign[] | null>;

  /**
   * Observable that emits whenever the current campaign changes.
   * If there is no campaign active, `null` will be emitted.
   */
  public currentCampaign$: Observable<Campaign | null> = merge(
    this.onAction(CampaignActionName.UnsetCurrentCampaign).pipe(
      map(() => null),
    ),
    this.onAction(CampaignActionName.SetCurrentCampaign).pipe(
      switchMap(({ id }) =>
        this.store.select<Campaign>(`next.campaigns.${id}`).pipe(
          // Only emit when a campaign is available
          filter((campaign) => !!campaign),
          distinctUntilChanged(
            (prev, cur) => prev.versionHash === cur.versionHash,
          ),
        ),
      ),
    ),
  ).pipe(shareReplay(1));

  constructor() {
    // Ensures that a state is available for late subscribers for current campaign
    this.currentCampaign$.pipe(takeUntil(this._destroyed)).subscribe();

    // "Effect" that fetches the campaign when the current campaign ID changes
    this.onAction(CampaignActionName.SetCurrentCampaign)
      .pipe(
        switchMap(({ id }) => this.get(id).pipe(catchError(() => EMPTY))),
        takeUntil(this._destroyed),
      )
      .subscribe();

    this.allCampaigns$ = this.store
      .select<Campaign[] | null>('next.campaigns')
      .pipe(map((campaigns) => (campaigns ? Object.values(campaigns) : null)));
  }

  ngOnDestroy(): void {
    this._destroyed.next(true);
    this._destroyed.complete();
    this._actions.complete();
  }

  /**
   * Sets the current campaign
   *
   * @param campaignId ID of the campaign
   */
  setCurrentCampaignById(campaignId: string | number): void {
    this.dispatch({
      action: CampaignActionName.SetCurrentCampaign,
      payload: { id: `${campaignId}` },
    });
  }

  /**
   * Unsets the current campaign
   */
  public unsetCampaign(): void {
    this.dispatch({ action: CampaignActionName.UnsetCurrentCampaign });
  }

  /**
   * Dispatches the given `action`.
   *
   * @param action Action to dispatch
   */
  dispatch(action: CampaignStoreAction): void {
    this._actions.next(action);
  }

  /**
   * Emits with the Payload of the given action
   * @param action Action to filter for
   */
  onAction<Action extends CampaignActionName>(
    action: Action,
  ): Observable<ActionPayload<Action>>;
  onAction(
    action: CampaignActionName,
  ): Observable<CampaignStoreAction['payload']> {
    return this._actions.pipe(
      filter((a) => a.action === action),
      map((a) => a.payload),
    );
  }

  public retrieveList(): Observable<Campaign[]> {
    return this.campaignNextApi.list().pipe(
      tap((campaigns) =>
        this.store.set(
          'next.campaigns',
          campaigns.reduce((acc, n) => ({ ...acc, [n.id]: n }), {}),
        ),
      ),
      catchError((e) => {
        this._actions.next({
          action: CampaignActionName.FetchCampaignListError,
          payload: { error: e },
        });
        throw e;
      }),
    );
  }

  /**
   * Fetches the campaign with the given `id`.
   *
   * @param id Id of the campaign to fetch
   *
   * @returns
   * Observable that emits with the campaign for the given `id` then completes.
   */
  public get(id: string): Observable<Campaign> {
    return this.campaignNextApi.get(id).pipe(
      tap((next) => this.store.set(`next.campaigns.${id}`, next)),
      catchError((e) => {
        this._actions.next({
          action: CampaignActionName.FetchCampaignError,
          payload: { id, error: e },
        });
        throw e;
      }),
    );
  }
}
